import { Box, Button, ButtonGroup, FormControlSizes, FormGroup, Grid, Stack, Text, ToggleButton } from '@talos/kyoko';
import { AggregatedAssetSelect } from '../AggregatedMetric/AggregatedAssetSelect';
import { FundingRateBlotterView, type FundingRateUpdateMode } from './FundingRateBlotter.types';
import { FundingRateUpdateModeToggle } from './FundingRateUpdateModeToggle';
import { FUNDING_RATE_BLOTTER_VIEW_LABELS } from './tokens';

interface FundingRateBlotterConfigViewProps {
  selectedTalosSymbols: string[];
  availableTalosSymbols: string[];
  onSelectedTalosSymbolsChange: (talosSymbols: string[]) => void;
  updateMode: FundingRateUpdateMode;
  onUpdateModeChange: (mode: FundingRateUpdateMode) => void;
  defaultView: FundingRateBlotterView;
  onDefaultViewChange: (view: FundingRateBlotterView) => void;
  onClose: () => void;
}

/**
 * In-panel config view: an asset multi-select and the default settings (update mode and default
 * view). Both are live and editable at any time; changes persist immediately and also surface as a
 * toggle in the main toolbar. The panel is renamed via the tab context menu, so there is no title
 * field here.
 */
export function FundingRateBlotterConfigView({
  selectedTalosSymbols,
  availableTalosSymbols,
  onSelectedTalosSymbolsChange,
  updateMode,
  onUpdateModeChange,
  defaultView,
  onDefaultViewChange,
  onClose,
}: FundingRateBlotterConfigViewProps) {
  return (
    <>
      <Box
        px="spacing12"
        py="spacing8"
        borderColor="backgroundDivider"
        borderBottom="solid 1px"
        data-testid="funding-rate-blotter-config-header"
      >
        <Text fontSize="fontSizeMd">Funding Rate Blotter Settings</Text>
      </Box>
      <Stack p="spacing12" overflow="auto" scrollbarGutter="stable" pr="spacing4">
        <FormGroup label="Assets">
          <AggregatedAssetSelect
            availableTalosSymbols={availableTalosSymbols}
            selectedTalosSymbols={selectedTalosSymbols}
            onSelectedTalosSymbolsChange={onSelectedTalosSymbolsChange}
            buttonTestID="funding-rate-blotter-config-assets"
            dropdownTestID="funding-rate-blotter-config-assets-dropdown"
            // The config panel clips overflow; portal the menu so it isn't cut off by the panel bounds.
            portalize
          />
        </FormGroup>
        <FormGroup label="Update Mode">
          <FundingRateUpdateModeToggle
            updateMode={updateMode}
            onUpdateModeChange={onUpdateModeChange}
            groupTestId="funding-rate-blotter-config-update-mode"
            buttonTestIdPrefix="funding-rate-blotter-config-update-mode"
          />
        </FormGroup>
        <FormGroup label="Default View">
          <ButtonGroup size={FormControlSizes.Small} data-testid="funding-rate-blotter-config-default-view">
            {Object.values(FundingRateBlotterView).map(view => (
              <ToggleButton
                key={view}
                selected={defaultView === view}
                onClick={() => onDefaultViewChange(view)}
                data-testid={`funding-rate-blotter-config-default-view-${view}`}
              >
                {FUNDING_RATE_BLOTTER_VIEW_LABELS[view]}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </FormGroup>
      </Stack>
      <Grid
        mt="auto"
        gap="spacing8"
        p="spacing12"
        gridTemplateColumns="1fr"
        borderColor="backgroundDivider"
        borderTop="solid 1px"
      >
        <Button onClick={onClose} size={FormControlSizes.Small} data-testid="funding-rate-blotter-config-done">
          Done
        </Button>
      </Grid>
    </>
  );
}
